import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import BaseModal from '../common/BaseModal';
import {
  platnosciService, FORMY_PLATNOSCI, formaLabel, komunikatBledu
} from '../../services/platnosciService';

const MIN_POWOD = 5;
const MAX_POWOD = 200;

const FORMA_ICONS = {
  gotowka: 'fas fa-money-bill-wave',
  karta: 'fas fa-credit-card',
  blik: 'fas fa-mobile-alt',
  przelew: 'fas fa-university'
};

/**
 * Modal zmiany metody platnosci zakonczonej transakcji (tylko w otwartej zmianie).
 * Uzywany w Kasa/Bank (zakladka "Platnosci zmiany") i w POS (lista paragonow).
 *
 * @param {Object}   transakcja  { id, numer_paragonu, forma_platnosci, kwota?, fiskalizacja, formy? }
 * @param {Function} onSuccess   (wynik PUT) => void - wolane po udanej zmianie
 */
function ZmienPlatnoscModal({ isOpen, onClose, onSuccess, transakcja }) {
  const [nowaForma, setNowaForma] = useState('');
  const [powod, setPowod] = useState('');
  const [potwierdzamFiskalny, setPotwierdzamFiskalny] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNowaForma('');
      setPowod('');
      setPotwierdzamFiskalny(false);
      setError('');
      setLoading(false);
    }
  }, [isOpen, transakcja?.id]);

  if (!transakcja) return null;

  const obecna = transakcja.forma_platnosci || transakcja.obecna || '';
  const fiskalny = !!transakcja.fiskalizacja;
  const formyDostepne = (transakcja.formy && transakcja.formy.length ? transakcja.formy : FORMY_PLATNOSCI)
    .filter((f) => f !== obecna);

  const powodOk = powod.trim().length >= MIN_POWOD;
  const fiskalnyOk = !fiskalny || potwierdzamFiskalny;
  const canSubmit = !!nowaForma && powodOk && fiskalnyOk && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nowaForma) { setError('Wybierz nową formę płatności'); return; }
    if (!powodOk) { setError(`Powód musi mieć co najmniej ${MIN_POWOD} znaków`); return; }
    if (!fiskalnyOk) { setError('Potwierdź, że paragon fiskalny pozostaje z dotychczasową formą'); return; }

    setLoading(true);
    setError('');
    try {
      const wynik = await platnosciService.zmien(transakcja.id, {
        nowa_forma: nowaForma,
        powod: powod.trim(),
        potwierdzam_fiskalny: fiskalny ? potwierdzamFiskalny : false
      });
      const nr = wynik?.numer_paragonu || transakcja.numer_paragonu || `#${transakcja.id}`;
      const dopisek = wynik?.kp === 'dopisany' ? ' (dopisano KP)' : '';
      toast.success(`Płatność ${nr}: ${formaLabel(obecna)} → ${formaLabel(nowaForma)}${dopisek}`);
      onSuccess && onSuccess(wynik);
      onClose && onClose();
    } catch (err) {
      setError(komunikatBledu(err));
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px'
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Zmień płatność"
      subtitle={`Paragon ${transakcja.numer_paragonu || `#${transakcja.id}`}`}
      headerIcon="💱"
      size="md"
      headerStyle={{
        border: '1px solid var(--info)', color: 'var(--info)',
        borderRadius: '0.375rem 0.375rem 0 0'
      }}
    >
      <form onSubmit={handleSubmit}>
        {/* Obecna forma */}
        <div style={{
          marginBottom: '0.75rem', padding: '0.75rem', background: 'var(--bg)',
          borderRadius: '5px', border: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem'
        }}>
          <div>
            <div style={labelStyle}>Obecna forma płatności</div>
            <span
              data-testid="obecna-forma"
              style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                fontWeight: '600', backgroundColor: 'var(--info-light)', color: 'var(--info)'
              }}
            >
              {formaLabel(obecna)}
            </span>
          </div>
          {transakcja.kwota !== undefined && transakcja.kwota !== null && (
            <div style={{ textAlign: 'right' }}>
              <div style={labelStyle}>Kwota</div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>
                {Number(transakcja.kwota).toFixed(2)} zł
              </div>
            </div>
          )}
        </div>

        {/* Nowa forma */}
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Nowa forma płatności *</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }} role="group" aria-label="Nowa forma płatności">
            {formyDostepne.map((f) => {
              const active = nowaForma === f;
              return (
                <button
                  key={f}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setNowaForma(f); setError(''); }}
                  style={{
                    padding: '6px 12px', fontSize: '12px', fontWeight: active ? '600' : '500',
                    cursor: 'pointer',
                    // Standard V3SYS: zaznaczony przelacznik = INLINE tlo + bialy tekst
                    // (motyw wymusza !important na przyciskach bez inline background)
                    backgroundColor: active ? 'var(--primary)' : 'var(--card-bg)',
                    color: active ? '#fff' : 'var(--text-secondary)',
                    border: active ? '1px solid var(--primary)' : '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: '0.4rem'
                  }}
                >
                  <i className={FORMA_ICONS[f] || 'fas fa-money-check'} style={{ fontSize: '11px' }}></i>
                  {formaLabel(f)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Powod */}
        <div style={{ marginBottom: '0.75rem' }}>
          <label htmlFor="zmien-platnosc-powod" style={labelStyle}>
            Powód * (min. {MIN_POWOD} znaków)
          </label>
          <textarea
            id="zmien-platnosc-powod"
            value={powod}
            onChange={(e) => { setPowod(e.target.value); setError(''); }}
            rows="2"
            maxLength={MAX_POWOD}
            placeholder="np. klient płacił kartą, nabito gotówkę"
            style={{
              width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)',
              borderRadius: '4px', fontSize: '12px', resize: 'vertical',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)'
            }}
          />
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', textAlign: 'right' }}>
            {powod.trim().length}/{MAX_POWOD}
          </div>
        </div>

        {/* Ostrzezenie fiskalne */}
        {fiskalny && (
          <div
            data-testid="ostrzezenie-fiskalne"
            style={{
              marginBottom: '0.75rem', padding: '0.75rem',
              background: 'var(--danger-light)', border: '1px solid var(--danger)',
              borderRadius: '5px', color: 'var(--danger)', fontSize: '11px'
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '0.4rem' }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.4rem' }}></i>
              Paragon fiskalny jest już wydrukowany z formą „{formaLabel(obecna)}”
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              W systemie zostanie {nowaForma ? `„${formaLabel(nowaForma)}”` : 'nowa forma'}, na rolce pozostaje „{formaLabel(obecna)}”.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={potwierdzamFiskalny}
                onChange={(e) => { setPotwierdzamFiskalny(e.target.checked); setError(''); }}
                style={{ accentColor: 'var(--danger)' }}
              />
              Rozumiem, paragon fiskalny zostaje z formą {formaLabel(obecna)}
            </label>
          </div>
        )}

        {/* Blad */}
        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--danger)', background: 'var(--danger-light)', border: '1px solid var(--danger-light)',
              borderRadius: '4px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '11px'
            }}
          >
            <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
            {error}
          </div>
        )}

        {/* Przyciski */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: '6px 12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
              fontWeight: '500', fontSize: '12px', cursor: 'pointer'
            }}
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '6px 12px',
              backgroundColor: canSubmit ? 'var(--info)' : 'var(--info-light)',
              color: '#fff', fontWeight: '600', fontSize: '12px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.7
            }}
          >
            {loading ? 'Zapisywanie...' : '💱 Zmień płatność'}
          </button>
        </div>
      </form>
    </BaseModal>
  );
}

export default ZmienPlatnoscModal;
